import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { rentalService } from '../../services/rentalService';
import productService from '../../services/productService';
import stockMovementService from '../../services/stockMovementService';
import type { RentalContract, CreateRefillVisitData, RefillDetailData, Product } from '../../types/rental';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
  fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52',
  outline: 'none', colorScheme: 'light', boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontFamily: "'Inter', sans-serif", fontSize: 9,
  fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase',
  color: '#6b6a5c', marginBottom: 6,
};
const errorMsg: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a', marginTop: 4,
};
const ghostBtn: React.CSSProperties = {
  padding: '10px 24px', background: 'none',
  border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
  fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 400,
  letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', cursor: 'pointer',
};
const primaryBtn: React.CSSProperties = {
  padding: '10px 24px', background: 'linear-gradient(135deg, #d97706, #b45309)',
  border: 'none', borderRadius: 4, fontFamily: "'Inter', sans-serif", fontSize: 9,
  fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase',
  color: '#1c2033', cursor: 'pointer',
};
const itemRowStyle: React.CSSProperties = {
  background: '#faf7ee', border: '1px solid rgba(92,78,51,0.2)',
  borderRadius: 6, padding: 16,
};
const sectionTitle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500,
  letterSpacing: '0.3em', textTransform: 'uppercase', color: '#d97706',
};

interface RefillFormProps {
  contractId?: string;
  visitData?: {
    visitId?: string;
    contractId?: string;
    customerId?: string;
    customerName?: string;
    visitDate?: string;
    machines?: any[];
    employeeId?: string;
  };
  onSubmit: (data: CreateRefillVisitData) => void;
  onCancel: () => void;
}

interface RefillDetail {
  machineId: string;
  serialNumber: string;
  warehouseId: string;
  oilProductId: string;
  oilScentName: string;
  amountML: string;
  notes: string;
}

export const RefillForm: React.FC<RefillFormProps> = ({ contractId, visitData, onSubmit, onCancel }) => {
  const [selectedContractId, setSelectedContractId] = useState(contractId || visitData?.contractId || '');
  const [formData, setFormData] = useState({
    visitDate: visitData?.visitDate ? new Date(visitData.visitDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    employeeId: visitData?.employeeId || '',
    notes: ''
  });

  const [refillDetails, setRefillDetails] = useState<RefillDetail[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [warehouseStock, setWarehouseStock] = useState<Record<string, Record<string, number>>>({});

  const { data: contractsData } = useQuery({
    queryKey: ['rental-contracts', { status: 'ACTIVE' }],
    queryFn: () => rentalService.getRentalContracts({ status: 'ACTIVE' as any, limit: 100 })
  });

  const { data: contractData, isLoading: contractLoading } = useQuery({
    queryKey: ['rental-contract', selectedContractId],
    queryFn: () => rentalService.getRentalContract(selectedContractId),
    enabled: !!selectedContractId
  });

  const { data: productsData } = useQuery({
    queryKey: ['products', { itemType: 'INVENTORY', limit: 100 }],
    queryFn: async () => {
      const response = await productService.getProducts({ itemType: 'INVENTORY', limit: 100, isActive: true });
      return response.data || [];
    }
  });

  const { data: warehousesData } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => stockMovementService.getWarehouses()
  });

  const contracts = contractsData?.data || [];
  const contract = contractData;
  const oilProducts = productsData || [];
  const warehouses = warehousesData || [];

  useEffect(() => {
    if (visitData?.machines && visitData.machines.length > 0) {
      setRefillDetails(visitData.machines.map(machine => ({
        machineId: machine.id,
        serialNumber: machine.serialNumber?.serialNumber || machine.serialNumber || '',
        warehouseId: '',
        oilProductId: '',
        oilScentName: '',
        amountML: '0',
        notes: ''
      })));
    } else if (contract?.machines) {
      const rentedMachines = contract.machines.filter(m => m.status === 'RENTED');
      setRefillDetails(rentedMachines.map(machine => ({
        machineId: machine.id,
        serialNumber: machine.serialNumber?.serialNumber || '',
        warehouseId: '',
        oilProductId: '',
        oilScentName: '',
        amountML: '0',
        notes: ''
      })));
    }
  }, [contract, visitData]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleDetailChange = async (index: number, field: keyof RefillDetail, value: string) => {
    setRefillDetails(prev => {
      const newDetails = [...prev];
      newDetails[index] = { ...newDetails[index], [field]: value };
      if (field === 'oilProductId') {
        const selectedProduct = oilProducts.find((p: Product) => p.id === value);
        if (selectedProduct) {
          newDetails[index].oilScentName = selectedProduct.name;
        }
      }
      return newDetails;
    });

    if (field === 'warehouseId' && value) {
      try {
        const stockData = await stockMovementService.getWarehouseStock(value, 'SERVICE');
        const stockMap: Record<string, number> = {};
        stockData.stock.forEach(item => {
          stockMap[item.productId] = item.quantityOnHand * 500;
        });
        setWarehouseStock(prev => ({ ...prev, [value]: stockMap }));
      } catch (error) {
        console.error('Error fetching warehouse stock:', error);
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!selectedContractId) newErrors.contract = 'Contract is required';
    if (!formData.visitDate) newErrors.visitDate = 'Visit date is required';
    if (!formData.employeeId) newErrors.employeeId = 'Employee is required';

    const hasRefillData = refillDetails.some(detail =>
      detail.oilProductId && parseFloat(detail.amountML) > 0
    );
    if (!hasRefillData) newErrors.details = 'At least one machine must have refill data';

    refillDetails.forEach((detail, index) => {
      if (detail.oilProductId || parseFloat(detail.amountML) > 0) {
        if (!detail.warehouseId) newErrors[`detail_${index}_warehouse`] = 'Warehouse is required';
        if (!detail.oilProductId) newErrors[`detail_${index}_product`] = 'Oil product is required';
        if (!detail.amountML || parseFloat(detail.amountML) <= 0) newErrors[`detail_${index}_amount`] = 'Amount must be greater than 0';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const details: RefillDetailData[] = refillDetails
      .filter(detail => detail.oilProductId && parseFloat(detail.amountML) > 0)
      .map(detail => ({
        machineId: detail.machineId,
        warehouseId: detail.warehouseId,
        oilProductId: detail.oilProductId,
        oilScentName: detail.oilScentName,
        amountML: parseFloat(detail.amountML),
        notes: detail.notes || undefined
      }));

    const submitData: CreateRefillVisitData = {
      contractId: selectedContractId,
      visitDate: formData.visitDate,
      employeeId: formData.employeeId,
      notes: formData.notes || undefined,
      details
    };

    onSubmit(submitData);
  };

  const totalOilUsed = refillDetails.reduce((sum, detail) =>
    sum + parseFloat(detail.amountML || '0'), 0
  );

  return (
    <form onSubmit={handleSubmit} style={{
      background: '#ffffff', border: '1px solid rgba(92,78,51,0.22)',
      borderRadius: 8, overflow: 'hidden',
      display: 'flex', flexDirection: 'column', gap: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 28px', borderBottom: '1px solid rgba(92,78,51,0.15)',
      }}>
        <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 300, color: '#1c2033', margin: 0 }}>
          Record Refill Visit
        </h3>
      </div>

      <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Customer info from visit completion */}
        {visitData?.customerName && (
          <div style={{
            background: '#faf7ee', border: '1px solid rgba(92,78,51,0.2)',
            borderRadius: 6, padding: 12,
          }}>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: 0 }}>
              Customer: <span style={{ color: '#5b5a52', fontWeight: 500 }}>{visitData.customerName}</span>
            </p>
          </div>
        )}

        {/* Contract Selection */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={labelStyle}>Rental Contract <span style={{ color: '#c95a5a' }}>*</span></label>
            <select
              value={selectedContractId}
              onChange={(e) => {
                setSelectedContractId(e.target.value);
                if (errors.contract) setErrors(prev => ({ ...prev, contract: '' }));
              }}
              style={errors.contract ? { ...inputStyle, border: '1px solid rgba(201,90,90,0.5)' } : inputStyle}
              disabled={!!contractId || !!visitData}
            >
              <option value="">Select Contract</option>
              {contracts.map((c: RentalContract) => (
                <option key={c.id} value={c.id}>
                  {c.contractNumber} - {c.customer?.name}
                </option>
              ))}
            </select>
            {errors.contract && <p style={errorMsg}>{errors.contract}</p>}
          </div>

          <div>
            <label style={labelStyle}>Visit Date <span style={{ color: '#c95a5a' }}>*</span></label>
            <input
              type="date"
              value={formData.visitDate}
              onChange={(e) => handleInputChange('visitDate', e.target.value)}
              style={errors.visitDate ? { ...inputStyle, border: '1px solid rgba(201,90,90,0.5)' } : inputStyle}
            />
            {errors.visitDate && <p style={errorMsg}>{errors.visitDate}</p>}
          </div>
        </div>

        {/* Employee ID */}
        <div>
          <label style={labelStyle}>Employee ID <span style={{ color: '#c95a5a' }}>*</span></label>
          <input
            type="text"
            value={formData.employeeId}
            onChange={(e) => handleInputChange('employeeId', e.target.value)}
            placeholder="Enter employee ID"
            style={errors.employeeId ? { ...inputStyle, border: '1px solid rgba(201,90,90,0.5)' } : inputStyle}
          />
          {errors.employeeId && <p style={errorMsg}>{errors.employeeId}</p>}
        </div>

        {/* Visit Notes */}
        <div>
          <label style={labelStyle}>Visit Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            rows={2}
            placeholder="Add any notes about this visit..."
            style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
          />
        </div>

        {/* Refill Details per Machine */}
        {contractLoading && (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c' }}>Loading contract details...</p>
          </div>
        )}

        {!contractLoading && refillDetails.length === 0 && selectedContractId && (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c' }}>No machines available for refill in this contract.</p>
          </div>
        )}

        {refillDetails.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={sectionTitle}>Refill Details per Machine</div>
            {errors.details && <p style={{ ...errorMsg, margin: 0 }}>{errors.details}</p>}

            {refillDetails.map((detail, index) => (
              <div key={detail.machineId} style={itemRowStyle}>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#d97706', marginBottom: 12 }}>
                  Machine: {detail.serialNumber}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Warehouse <span style={{ color: '#c95a5a' }}>*</span></label>
                    <select
                      value={detail.warehouseId}
                      onChange={(e) => handleDetailChange(index, 'warehouseId', e.target.value)}
                      style={errors[`detail_${index}_warehouse`] ? { ...inputStyle, border: '1px solid rgba(201,90,90,0.5)' } : inputStyle}
                    >
                      <option value="">Select Warehouse</option>
                      {warehouses.map((warehouse) => (
                        <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
                      ))}
                    </select>
                    {errors[`detail_${index}_warehouse`] && (
                      <p style={errorMsg}>{errors[`detail_${index}_warehouse`]}</p>
                    )}
                  </div>

                  <div>
                    <label style={labelStyle}>Oil Scent Product</label>
                    <select
                      value={detail.oilProductId}
                      onChange={(e) => handleDetailChange(index, 'oilProductId', e.target.value)}
                      style={errors[`detail_${index}_product`] ? { ...inputStyle, border: '1px solid rgba(201,90,90,0.5)' } : { ...inputStyle, opacity: !detail.warehouseId ? 0.5 : 1 }}
                      disabled={!detail.warehouseId}
                    >
                      <option value="">Select Oil Scent</option>
                      {oilProducts.map((product: Product) => (
                        <option key={product.id} value={product.id}>
                          {product.name}
                          {detail.warehouseId && warehouseStock[detail.warehouseId]?.[product.id] !== undefined
                            ? ` (${warehouseStock[detail.warehouseId][product.id]} ML available)`
                            : ''}
                        </option>
                      ))}
                    </select>
                    {errors[`detail_${index}_product`] && (
                      <p style={errorMsg}>{errors[`detail_${index}_product`]}</p>
                    )}
                    {detail.warehouseId && detail.oilProductId && warehouseStock[detail.warehouseId]?.[detail.oilProductId] !== undefined && (
                      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#5ac98c', marginTop: 4 }}>
                        Available: {warehouseStock[detail.warehouseId][detail.oilProductId]} ML
                      </p>
                    )}
                  </div>

                  <div>
                    <label style={labelStyle}>Amount (ML)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={detail.amountML}
                      onChange={(e) => handleDetailChange(index, 'amountML', e.target.value)}
                      style={errors[`detail_${index}_amount`] ? { ...inputStyle, border: '1px solid rgba(201,90,90,0.5)' } : { ...inputStyle, opacity: !detail.oilProductId ? 0.5 : 1 }}
                      disabled={!detail.oilProductId}
                    />
                    {errors[`detail_${index}_amount`] && (
                      <p style={errorMsg}>{errors[`detail_${index}_amount`]}</p>
                    )}
                  </div>

                  <div>
                    <label style={labelStyle}>Notes</label>
                    <input
                      type="text"
                      value={detail.notes}
                      onChange={(e) => handleDetailChange(index, 'notes', e.target.value)}
                      placeholder="Optional notes..."
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary */}
        {refillDetails.length > 0 && totalOilUsed > 0 && (
          <div style={{
            background: 'rgba(241,245,249,1)', border: '1px solid rgba(92,78,51,0.22)',
            borderRadius: 6, padding: '20px 24px',
          }}>
            <div style={{ ...sectionTitle, marginBottom: 10 }}>Refill Summary</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c' }}>Total Oil Used</span>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 24, fontWeight: 300, color: '#fbbf24' }}>
                {totalOilUsed.toFixed(2)} ML
              </span>
            </div>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#8a8778', marginTop: 6 }}>
              {refillDetails.filter(d => d.oilProductId && parseFloat(d.amountML) > 0).length} machine(s) will be refilled
            </p>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 4 }}>
          <button type="button" onClick={onCancel} style={ghostBtn}>Cancel</button>
          <button type="submit" style={primaryBtn}>Record Refill Visit</button>
        </div>
      </div>
    </form>
  );
};
